import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Polygon from '@arcgis/core/geometry/Polygon';
import { Select, Typography } from 'antd';
import Checkbox from "antd/lib/checkbox/Checkbox";
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { MapContext } from '../../../../App/MapContextContainer';
import TabHeader from "../../../../components/Tabs/TabHeader";
import TabPane from "../../../../components/Tabs/TabPane";
import Tabs from "../../../../components/Tabs/Tabs";
import { HistoryContext } from '../../HistoryContextContainer';
import { municipalityIdsAndNames } from '../../data/municipality_id_and_names';
import {
    FilterOutlined,
    TableOutlined,
    QuestionOutlined
} from '@ant-design/icons';
import HelpTab from "./HelpTab";
import FiltersTab from "../../../../components/Tabs/FiltersTab";
import SummaryTab from "./SummaryTab";
import { sortByNameFn } from "./sortByNameFn";
import styled from "styled-components";
import ShareTab, { ShareTabIcon } from "../../../../components/Tabs/ShareTab";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const TopPartContainer = styled.div`
    padding-bottom: 1em;
`;

export default function StatisticsView() {
    const historyContext = useContext(HistoryContext);
    var { legendData } = useContext(MapContext);
    const [groupedStatistics, setGroupedStatistics] = useState<any[]>([]);
    const mapContext = useContext(MapContext);
    const [areaSizeInHectares, setAreaSizeInHectares] = useState<number | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedAreaId, setSelectedAreaId] = useState<undefined | number>(undefined);
    const [errorText, setErrorText] = useState("");
    const { t, i18n } = useTranslation(["history", "sidePanelTabs"], { useSuspense: true });

    useEffect(() => {
        const grouped: any[] = historyContext.statistics.reduce((prev: any[], curr) => {
            const type = curr.attributes.CUTTINGREALIZATIONPRACTICE;
            let mainTypeItem = legendData?.find(l => l.values.includes(type.toString()));

            const mainType = mainTypeItem === undefined ?
                t("tuntematonHakkuutyyppi") :
                i18n.language === "fi" ? mainTypeItem?.nameFi : mainTypeItem?.nameEn;

            const existingMainType = prev.find((p: any) => p.type === mainType);

            if (existingMainType) {
                existingMainType.sumArea += curr.attributes.sumArea;

                if (curr.attributes.avgAge) {
                    existingMainType.avgAge += curr.attributes.avgAge;
                    existingMainType.typeCount += 1;
                }
            }
            else {
                prev.push({ type: mainType, sumArea: curr.attributes.sumArea, avgAge: curr.attributes.avgAge ?? 0, typeCount: curr.attributes.avgAge ? 1 : 0 });
            }

            return prev;
        }, []);

        const ageAveraged = grouped.map(g => {
            let averageAge = g.avgAge;

            if (g.typeCount > 1) {
                averageAge = g.avgAge / g.typeCount;
            }

            return {
                ...g,
                avgAge: averageAge
            }
        });

        setGroupedStatistics(ageAveraged);
    }, [historyContext.statistics]);

    var findAreaGeometry = async (areaId: number) => {
        try {
            setIsLoading(true);
            setErrorText("");

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/municipalities/${areaId}`, {
                method: "GET"
            });

            const json = await res.json();

            setIsLoading(false);

            return json.coordinates;
        } catch (error) {
            console.log("Error getting geometry for area");

            setErrorText(t("virheHaettaessaAlueenTietoja"));

            setIsLoading(false);
        }
    }

    var onAreaSelected = async (params: number) => {
        setSelectedAreaId(params);

        const areaGeometry = await findAreaGeometry(params);
        setGroupedStatistics([]);

        let polygon: Polygon;

        if (areaGeometry.length === 1) {
            polygon = new Polygon({
                rings: areaGeometry[0]
            });
        }
        else {
            const rings: number[][][] = [];

            areaGeometry.forEach((multipolygonPart: number[][][]) => {
                rings.push(multipolygonPart[0]);
            });

            polygon = new Polygon({
                rings: rings
            });
        }

        setAreaSizeInHectares(geometryEngine.geodesicArea(polygon, "hectares"));

        mapContext.navigateToPoint(polygon);
        historyContext.setAreaToInspect(polygon);
    }

    const tabHeaders: ReactElement[] = [
        <TabHeader step={0} key={0}><TableOutlined /></TabHeader>,
        <TabHeader step={1} key={1}><QuestionOutlined /></TabHeader>,
        <TabHeader step={2} key={2}><FilterOutlined /></TabHeader>,
        <TabHeader step={3} key={3}><ShareTabIcon /></TabHeader>
    ];

    return (
        <>
            <Typography.Title level={3}>{t("otsikko")}</Typography.Title>

            <TopPartContainer>
                <p>
                    {t("kuvaus")}
                </p>
                <Typography.Title level={5}>{t("valitseAlueLabel")}</Typography.Title>
                <Select
                    size="large"
                    placeholder={t("sidePanelTabs:valitsePlaceHolder")}
                    onFocus={() => window.blur()}
                    style={{ "width": "100%" }}
                    showSearch
                    defaultValue={selectedAreaId}
                    loading={isLoading}
                    filterOption={(input, option: any) => option && option.children?.toLowerCase().includes(input.toLowerCase())}
                    onSelect={onAreaSelected}
                >
                    {
                        municipalityIdsAndNames.sort(sortByNameFn).map((municipality) => (
                            <Option value={municipality.id} key={municipality.id}>{municipality.namefin}</Option>
                        ))
                    }
                </Select>

                {errorText !== "" && <Typography.Title type="danger" level={5}>{errorText}</Typography.Title>}

                <Checkbox style={{ "paddingTop": "1em" }}
                    checked={historyContext.showNotificationsOnMap}
                    onChange={() => historyContext.setShowNotificationsOnMap(!historyContext.showNotificationsOnMap)}>
                    {t("näytäIlmoituksetKartalla")}
                </Checkbox>
            </TopPartContainer>
            <Tabs tabHeaders={tabHeaders}>
                <TabPane number={0}>
                    <SummaryTab areaSizeInHectares={areaSizeInHectares} groupedStatistics={groupedStatistics} />
                </TabPane>
                <TabPane number={1}>
                    <HelpTab />
                </TabPane>
                <TabPane number={2}>
                    <FiltersTab hideTooltips={true} />
                </TabPane>
                <ShareTab number={3} />
            </Tabs>

            {/* <Typography.Title level={5}>Valitse alueen tyyppi, jota haluat tarkastella</Typography.Title>
            <Select
                placeholder="Valitse..."
                onFocus={() => window.blur()}
            >
                <Option value={"Kunta"} key={"Kunta"}>Kunta</Option>
            </Select> */}
        </>
    )
}